import { CompositeLayer } from '@deck.gl/core'
import { PathLayer, IconLayer } from '@deck.gl/layers'
import { groupData } from '@/js/analyze/GroupData.js'

const defaultProps = {
  ...CompositeLayer.defaultProps,
  groupKey: 'group',
  timeKey: 'time',
  highlightedUserId: { type: 'string', value: null },
  currentTime: { type: 'number', value: 0 },
}

/**
 * データがソート済みであることを前提とする二分探索（バイナリサーチ）を用いて、
 * conditionに合致する最後の要素のインデックスを効率的に検索する。
 * @param {Array.<Object>} data 元データ
 * @param {Function} condition 条件関数
 * @param {Number} [offset=0] 前回計算のindexオフセット
 */
const findLastIndexByCondition = (data, condition, offset = 0) => {
  if (!data.length) return -1
  if (data.length === 1) {
    // この段階で false なら全て false なので -1 を返す
    return condition(data[0]) ? offset : -1
  }
  const center = Math.ceil(data.length / 2)
  let newData
  let newOffset
  if (condition(data[center])) {
    newData = data.slice(center)
    newOffset = offset + center
  } else {
    newData = data.slice(0, center)
    newOffset = offset
  }
  return findLastIndexByCondition(newData, condition, newOffset)
}

/**
 * 軌跡再生用カスタムレイヤー
 *
 * [ハイライト実装アプローチ]
 * このレイヤーは、再生機能（`currentTime`の変更）による頻繁な再描画が想定されるため、
 * パフォーマンスを重視したアプローチを採用している。
 * `updateState`内で一度だけデータを「通常用」と「ハイライト用」に分割し、`state`に保持する。
 * `renderLayers`では、ハイライト用レイヤーを通常用レイヤーの後に描画することで、
 * 高コストなソート処理を避けつつ、ハイライト対象を最前面に表示する。
 */
class LocationPlayCompositeLayer extends CompositeLayer {
  /**
   * state更新
   */
  updateState({ props, oldProps, context, changeFlags }) {
    super.updateState({ props, oldProps, context, changeFlags })
    const { dataChanged, propsChanged } = changeFlags
    const shouldUpdateData =
      dataChanged ||
      (propsChanged &&
        (props.groupKey !== oldProps.groupKey ||
          props.highlightedUserId !== oldProps.highlightedUserId))
    // data変更 or groupKey変更で軌跡データを更新
    if (shouldUpdateData) {
      this._updateTrackData()
      this._updateLatestIndex()
      this.setNeedsRedraw()
    }

    // currentTime 変更で直近index値を更新
    if (propsChanged) {
      if (shouldUpdateData) return // 上のブロックで処理済みのため
      if (
        props.currentTime !== oldProps.currentTime ||
        props.timeKey !== oldProps.timeKey
      ) {
        this._updateLatestIndex()
        this.setNeedsRedraw()
      }
    }
  }

  /**
   * 軌跡データ更新
   */
  _updateTrackData() {
    const { data, groupKey, highlightedUserId } = this.props
    const trackData = groupData(data, groupKey)

    const [othersTrackData, highlightedTrackData] =
      highlightedUserId !== null
        ? trackData.reduce(
            ([o, h], item) => {
              if (item[groupKey] === highlightedUserId) {
                h.push(item)
              } else {
                o.push(item)
              }
              return [o, h]
            },
            [[], []]
          )
        : [trackData, []]

    this.setState({ othersTrackData, highlightedTrackData })
  }

  /**
   * 直近index更新
   */
  _updateLatestIndex() {
    const { othersTrackData, highlightedTrackData } = this.state
    if (!othersTrackData) return

    const { timeKey, currentTime } = this.props

    const updateLatestIndex = (data) =>
      data.map((datum) => ({
        ...datum,
        latestIndex: findLastIndexByCondition(
          datum.data,
          (d) => d[timeKey] < currentTime
        ),
      }))

    this.setState({
      othersTrackData: updateLatestIndex(othersTrackData),
      highlightedTrackData: updateLatestIndex(highlightedTrackData || []),
    })
  }

  renderLayers() {
    const { othersTrackData, highlightedTrackData } = this.state
    return [
      // --- 通常表示用レイヤー ---

      // 軌跡
      new PathLayer(
        this.props,
        { getPath: (d) => d.data.map((v) => v.coordinates) },
        this.getSubLayerProps({ id: 'trail' }),
        {
          id: `${this.props.id}-trail-others`,
          data: othersTrackData,
        }
      ),
      // アイコン
      new IconLayer(
        this.props,
        { getPosition: (d) => [d.data[0].lng, d.data[0].lat] },
        this.getSubLayerProps({ id: 'head' }),
        {
          id: `${this.props.id}-head-others`,
          data: othersTrackData,
        }
      ),

      // --- ハイライト用レイヤー ---
      // 通常レイヤーの後に描画することで、最前面に表示される

      // 軌跡
      new PathLayer(
        this.props,
        { getPath: (d) => d.data.map((v) => v.coordinates) },
        this.getSubLayerProps({ id: 'trail' }),
        {
          id: `${this.props.id}-trail-highlighted`,
          data: highlightedTrackData,
        }
      ),
      // アイコン
      new IconLayer(
        this.props,
        { getPosition: (d) => [d.data[0].lng, d.data[0].lat] },
        this.getSubLayerProps({ id: 'head' }),
        {
          id: `${this.props.id}-head-highlighted`,
          data: highlightedTrackData,
        }
      ),
    ]
  }
}

LocationPlayCompositeLayer.layerName = 'LocationPlayCompositeLayer'
LocationPlayCompositeLayer.defaultProps = defaultProps

export { LocationPlayCompositeLayer }
